import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import {
  CircleAlert, Clock, CornerDownLeft, DraftingCompass, FileText, FolderKanban, ListChecks, Loader2, Package,
  ReceiptText, Search, ShoppingCart,
} from "lucide-react"

import api from "@/lib/api"
import { cn } from "@/lib/utils"
import { getErrorMessage } from "@/lib/errors"
import { readStored, storageKey, writeStored } from "@/lib/storage"
import { useAuth } from "@/contexts/AuthContext"
import { PALETTE_NAV_STATE, usePaletteNavKey, useSamePageAwareNavigate } from "@/hooks/usePaletteNav"
import { StatusPill } from "@/components/ui/status-pill"

/*
  CommandPalette — ⌘K / Ctrl+K: search everything this user may open (GET /api/search), recent items, quick
  actions and "Go to" pages, all from the keyboard (02 #9). Mounted once by the shell (Layout.js):

    const palette = useCommandPalette();                       // binds ⌘K / Ctrl+K on the whole page
    <CommandPaletteTrigger onClick={palette.show} />            // the top-bar search box "Search projects, tasks, items… ⌘K"
    <CommandPalette
      open={palette.open} onOpenChange={palette.setOpen}
      pages={filteredNav.map(({ label, path, icon }) => ({ label, path, icon }))}     // "Go to" (already permission-filtered)
      actions={[
        { id: 'new-project', label: 'New project', icon: Plus, to: '/projects?new=1', keywords: ['create'] },
        { id: 'new-task', label: 'New task', icon: ListPlus, onSelect: () => openTaskDialog() },
      ]} />

  - Typing 2+ characters searches the server (debounced 180 ms, older requests cancelled); results are grouped
    by kind (Projects, Tasks, Shop drawings, Catalog items, LPOs, Invoices, Documents — only kinds the role may
    open; the server enforces it). Actions and pages that match the text are listed under the results.
  - Empty box: recent items (the last 6 opened from the palette, per user, this browser), quick actions, pages.
  - ↑/↓ move, Enter opens, Esc closes. A result opens its `url` with the router.
  - Only results for exactly the text in the box can be opened: while the next answer is on its way the previous
    results stay dimmed (not selectable), and an Enter pressed meanwhile waits for the answer and opens its first row.
  - A link to the page you are already on (e.g. an LPO result while on /procurement) navigates with
    `state.paletteNav` set; the shell keys the routed page on `usePaletteNavKey()` (App.js does — see
    hooks/usePaletteNav.js), so the page remounts and reads its deep link (`?lpo=`, `?tab=stock&search=`) like on
    a fresh load.
  Props: open, onOpenChange, pages [{ label, path, icon, keywords }], actions [{ id, label, icon, to | onSelect,
  shortcut, keywords, hint }], placeholder, searchLimit (5), testId ('command-palette').
*/

export const SEARCH_TYPE_META = {
  project: { label: "Project", plural: "projects", icon: FolderKanban },
  task: { label: "Task", plural: "tasks", icon: ListChecks },
  shop_drawing: { label: "Shop drawing", plural: "shop drawings", icon: DraftingCompass },
  item: { label: "Catalog item", plural: "catalog items", icon: Package },
  lpo: { label: "LPO", plural: "LPOs", icon: ShoppingCart },
  invoice: { label: "Invoice", plural: "invoices", icon: ReceiptText },
  document: { label: "Document", plural: "documents", icon: FileText },
}

const MIN_QUERY = 2
const DEBOUNCE_MS = 180
const MAX_RECENT = 6

const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "")

/** "⌘K" on a Mac, "Ctrl K" elsewhere. */
export const commandPaletteShortcutLabel = () => (isMac() ? "⌘K" : "Ctrl K")

/** Open state + the global ⌘K / Ctrl+K shortcut (toggles). */
export function useCommandPalette({ enabled = true } = {}) {
  const [open, setOpen] = React.useState(false)
  React.useEffect(() => {
    if (!enabled) return undefined
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.isComposing || event.altKey || event.shiftKey) return
      if (!(event.metaKey || event.ctrlKey) || String(event.key).toLowerCase() !== "k") return
      event.preventDefault()
      setOpen((current) => !current)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [enabled])
  const show = React.useCallback(() => setOpen(true), [])
  const hide = React.useCallback(() => setOpen(false), [])
  return { open, setOpen, show, hide }
}

/**
 * The top-bar search box that opens the palette (an icon button below `md`). Both buttons get the event
 * handlers (`onClick`…); `className` and `ref` go to the md+ box, `iconClassName` to the phone icon button;
 * `data-testid` names the box and `<data-testid>-icon` the icon button.
 */
const CommandPaletteTrigger = React.forwardRef(({
  className,
  iconClassName,
  label = "Search projects, tasks, items…",
  "data-testid": testId = "command-palette-trigger",
  ...props
}, ref) => (
  <>
    <button
      ref={ref}
      type="button"
      aria-label="Search everything"
      aria-keyshortcuts={isMac() ? "Meta+K" : "Control+K"}
      className={cn(
        "hidden h-9 w-[340px] max-w-full items-center gap-2 rounded-control border border-line bg-surface-2 px-2.5 text-left text-[13px] text-ink-3 transition-colors hover:border-[color:var(--brand-soft-border)] hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex",
        className
      )}
      {...props}
      data-testid={testId}
    >
      <Search className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <kbd className="ui-kbd ml-auto">{commandPaletteShortcutLabel()}</kbd>
    </button>
    <button
      type="button"
      aria-label="Search everything"
      className={cn(
        "grid h-9 w-9 place-items-center rounded-control text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden",
        iconClassName
      )}
      {...props}
      data-testid={`${testId}-icon`}
    >
      <Search className="h-[18px] w-[18px]" aria-hidden="true" />
    </button>
  </>
))
CommandPaletteTrigger.displayName = "CommandPaletteTrigger"

function matchesText(entry, query) {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [entry.label, ...(entry.keywords || [])].some((text) => String(text || "").toLowerCase().includes(q))
}

function Highlight({ text, query }) {
  const value = String(text || "")
  const q = query.trim()
  const index = q ? value.toLowerCase().indexOf(q.toLowerCase()) : -1
  if (index < 0) return value
  return (
    <>
      {value.slice(0, index)}
      <mark className="rounded-[3px] bg-[color:var(--brand-soft-border)] px-px text-inherit">{value.slice(index, index + q.length)}</mark>
      {value.slice(index + q.length)}
    </>
  )
}

function useRecentItems() {
  const auth = useAuth()
  const key = storageKey("palette", "recent", auth?.user?.id || "anon")
  const [items, setItems] = React.useState(() => readStored(key, []))
  React.useEffect(() => {
    setItems(readStored(key, []))
  }, [key])
  const remember = React.useCallback((item) => {
    const entry = { type: item.type, id: item.id, title: item.title, subtitle: item.subtitle, url: item.url, code: item.code }
    setItems((current) => {
      const next = [entry, ...(Array.isArray(current) ? current : []).filter((old) => !(old.type === entry.type && old.id === entry.id))]
        .slice(0, MAX_RECENT)
      writeStored(key, next)
      return next
    })
  }, [key])
  return [Array.isArray(items) ? items : [], remember]
}

function useServerSearch(query, { enabled, limit }) {
  const [state, setState] = React.useState({ loading: false, error: null, data: null, query: "" })
  React.useEffect(() => {
    const q = query.trim()
    if (!enabled || q.length < MIN_QUERY) {
      setState({ loading: false, error: null, data: null, query: "" })
      return undefined
    }
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: null }))
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.get("/search", { params: { q, limit }, signal: controller.signal })
        setState({ loading: false, error: null, data: res.data, query: q })
      } catch (error) {
        if (controller.signal.aborted || error?.name === "CanceledError") return
        setState({ loading: false, error, data: null, query: q })
      }
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, enabled, limit])
  return state
}

const itemClass =
  "flex min-h-[44px] cursor-pointer select-none items-center gap-3 rounded-[10px] px-3 py-2 text-[13.5px] text-ink outline-none data-[selected=true]:bg-surface-hover data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
const groupClass =
  "px-2 pb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em] [&_[cmdk-group-heading]]:text-ink-3"

function ResultRow({ item, query, onSelect, stale = false }) {
  const meta = SEARCH_TYPE_META[item.type] || { label: item.type, icon: Search }
  const Icon = meta.icon
  const showCode = item.code && item.code !== item.title
  return (
    <CommandPrimitive.Item
      value={`${item.type}:${item.id}`}
      onSelect={() => onSelect(item)}
      disabled={stale}
      className={cn(itemClass, stale && "data-[disabled=true]:opacity-100")}
      data-testid={`palette-result-${item.type}-${item.id}`}
      data-stale={stale ? "true" : undefined}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-2" aria-hidden="true">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className={cn("min-w-0 truncate font-semibold", item.type === "item" || item.type === "lpo" ? "font-mono text-[12.5px]" : "")}>
            <Highlight text={item.title} query={query} />
          </span>
          {showCode ? <span className="min-w-0 max-w-[45%] truncate font-mono text-[11.5px] text-ink-3"><Highlight text={item.code} query={query} /></span> : null}
        </span>
        {item.subtitle ? <span className="truncate text-[12.5px] text-ink-3">{item.subtitle}</span> : null}
      </span>
      <span className="sr-only">{meta.label}</span>
      {item.status ? <StatusPill status={item.status} domain={item.status_domain} size="sm" className="hidden sm:inline-flex" /> : null}
    </CommandPrimitive.Item>
  )
}

function EntryRow({ entry, query, onSelect, testPrefix }) {
  const Icon = entry.icon
  return (
    <CommandPrimitive.Item
      value={`${testPrefix}:${entry.id || entry.path || entry.label}`}
      onSelect={() => onSelect(entry)}
      className={itemClass}
      disabled={entry.disabled}
      data-testid={`palette-${testPrefix}-${entry.id || entry.path || entry.label}`}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-2" aria-hidden="true">
        {Icon ? <Icon className="h-4 w-4" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate font-semibold"><Highlight text={entry.label} query={query} /></span>
      {entry.hint ? <span className="hidden truncate text-[12.5px] text-ink-3 sm:inline">{entry.hint}</span> : null}
      {entry.shortcut ? <kbd className="ui-kbd">{entry.shortcut}</kbd> : null}
    </CommandPrimitive.Item>
  )
}

function CommandPalette({
  open,
  onOpenChange,
  pages = [],
  actions = [],
  placeholder = "Search projects, tasks, drawings, items, LPOs…",
  searchLimit = 5,
  testId = "command-palette",
}) {
  const go = useSamePageAwareNavigate()
  const [query, setQuery] = React.useState("")
  const [recent, remember] = useRecentItems()
  const search = useServerSearch(query, { enabled: open, limit: searchLimit })
  const trimmed = query.trim()
  const searching = trimmed.length >= MIN_QUERY
  // `fresh`: the server answered (results or an error) for exactly the text in the box. Until then the previous
  // answer is only shown dimmed and can never be opened (typing "LPO-2025-00" then "1" + Enter must not open a
  // result for the old text).
  const fresh = searching && search.query === trimmed
  const pending = searching && !fresh
  const data = fresh ? search.data : null
  const failed = fresh && Boolean(search.error)

  React.useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const close = () => onOpenChange?.(false)
  const openResult = (item) => {
    remember(item)
    close()
    go(item.url)
  }
  const runEntry = (entry) => {
    close()
    if (entry.onSelect) entry.onSelect()
    else go(entry.to || entry.path)
  }

  const groups = data?.groups || []
  const staleGroups = pending ? search.data?.groups || [] : []
  const matchingActions = actions.filter((entry) => !entry.hidden && matchesText(entry, trimmed))
  const matchingPages = pages.filter((entry) => matchesText(entry, trimmed))
  const nothing = Boolean(data) && !groups.length && !matchingActions.length && !matchingPages.length
  const searchedKinds = (data?.searched || []).map((type) => SEARCH_TYPE_META[type]?.plural).filter(Boolean)
  const waitingOnly = pending && !staleGroups.length && !matchingActions.length && !matchingPages.length

  // The highlighted row is controlled here: when results arrive (or the list changes), the first row is
  // highlighted, so Enter always opens what the person sees first. Stale rows are never in this list.
  const handlers = new Map()
  if (!searching) recent.forEach((item) => handlers.set(`${item.type}:${item.id}`, () => openResult(item)))
  groups.forEach((group) => group.items.forEach((item) => handlers.set(`${item.type}:${item.id}`, () => openResult(item))))
  matchingActions.filter((entry) => !entry.disabled)
    .forEach((entry) => handlers.set(`action:${entry.id || entry.path || entry.label}`, () => runEntry(entry)))
  matchingPages.filter((entry) => !entry.disabled).forEach((entry) => handlers.set(`page:${entry.path}`, () => runEntry(entry)))
  const values = [...handlers.keys()]
  const firstValue = values[0] || ""
  const [selected, setSelected] = React.useState("")
  const valuesKey = values.join("\n")
  React.useEffect(() => {
    setSelected(valuesKey ? valuesKey.split("\n")[0] : "")
  }, [valuesKey])

  // Enter while the answer for the current text is on its way: wait for it, then open its first row — unless
  // the person moved the highlight to an action / page on purpose (then cmdk opens that one right away).
  const handlersRef = React.useRef(handlers)
  React.useLayoutEffect(() => {
    handlersRef.current = handlers
  })
  const enterWaitingFor = React.useRef(null)
  const onRootKeyDown = (event) => {
    if (event.key !== "Enter" || event.nativeEvent?.isComposing || !pending) return
    if (selected && selected !== firstValue) return
    event.preventDefault()
    enterWaitingFor.current = trimmed
  }
  React.useEffect(() => {
    const waitingFor = enterWaitingFor.current
    if (waitingFor === null) return
    if (!open || waitingFor !== trimmed) {
      enterWaitingFor.current = null
      return
    }
    if (pending) return
    enterWaitingFor.current = null
    if (!failed && firstValue) handlersRef.current.get(firstValue)?.()
  }, [open, trimmed, pending, failed, firstValue])

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[color:var(--scrim)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-surface text-ink shadow-overlay focus:outline-none sm:inset-auto sm:left-1/2 sm:top-[12vh] sm:max-h-[min(640px,76vh)] sm:w-[min(640px,calc(100vw-32px))] sm:-translate-x-1/2 sm:rounded-card sm:border sm:border-line"
          data-testid={testId}
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Search and commands</DialogPrimitive.Title>
          <CommandPrimitive
            shouldFilter={false}
            loop
            label="Search and commands"
            value={selected}
            onValueChange={setSelected}
            onKeyDown={onRootKeyDown}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex items-center gap-2.5 border-b border-line px-4">
              {search.loading || pending ? (
                <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin text-ink-3" aria-hidden="true" />
              ) : (
                <Search className="h-[18px] w-[18px] shrink-0 text-ink-3" aria-hidden="true" />
              )}
              <CommandPrimitive.Input
                value={query}
                onValueChange={setQuery}
                placeholder={placeholder}
                className="h-14 min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-4"
                data-testid={`${testId}-input`}
              />
              <DialogPrimitive.Close
                aria-label="Close search"
                className="rounded-md px-1.5 py-1 text-[12px] font-semibold text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="hidden sm:inline" aria-hidden="true">Esc</span>
                <span className="sm:hidden" aria-hidden="true">Close</span>
              </DialogPrimitive.Close>
            </div>
            {/* Status, error and empty state sit outside the listbox (only options and groups belong in it). */}
            <div aria-live="polite" className="sr-only" data-testid={`${testId}-status`}>
              {pending ? "Searching…" : data ? `${data.total || 0} results` : ""}
            </div>
            {failed ? (
              <div className="mx-4 mt-3 flex items-start gap-2 rounded-control bg-status-red-bg px-3 py-2.5 text-[13px] text-status-red-ink" role="alert">
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span><b>Search is not available right now.</b> {getErrorMessage(search.error)}</span>
              </div>
            ) : null}
            {nothing ? (
              <div className="px-6 py-10 text-center" data-testid={`${testId}-empty`}>
                <p className="font-heading text-[15px] font-bold text-ink">No matches for “{trimmed}”</p>
                <p className="mt-1 text-[13px] text-ink-3">
                  {searchedKinds.length ? `Searched ${searchedKinds.join(", ")}.` : "Your role cannot open any searchable page."} Try a code like LPO-2025-001 or a part number.
                </p>
              </div>
            ) : null}
            {waitingOnly ? (
              <p className="px-6 py-8 text-center text-[13px] text-ink-3" aria-hidden="true" data-testid={`${testId}-searching`}>
                Searching for “{trimmed}”…
              </p>
            ) : null}
            <CommandPrimitive.List className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2 sm:max-h-[min(520px,62vh)]">
              {!searching && recent.length ? (
                <CommandPrimitive.Group heading="Recent" className={groupClass}>
                  {recent.map((item) => (
                    <ResultRow key={`recent-${item.type}-${item.id}`} item={{ ...item, id: item.id }} query="" onSelect={openResult} />
                  ))}
                </CommandPrimitive.Group>
              ) : null}

              {groups.map((group) => (
                <CommandPrimitive.Group key={group.type} heading={group.label} className={groupClass}>
                  {group.items.map((item) => (
                    <ResultRow key={`${item.type}-${item.id}`} item={item} query={trimmed} onSelect={openResult} />
                  ))}
                </CommandPrimitive.Group>
              ))}
              {/* the previous text's results while the new answer loads: dimmed as a group (the rows' own disabled
                  dimming is switched off), not selectable, hidden from assistive tech */}
              {staleGroups.map((group) => (
                <CommandPrimitive.Group
                  key={`stale-${group.type}`}
                  heading={group.label}
                  className={cn(groupClass, "opacity-60")}
                  aria-hidden="true"
                  data-testid={`${testId}-stale`}
                >
                  {group.items.map((item) => (
                    <ResultRow key={`stale-${item.type}-${item.id}`} item={item} query={search.query} onSelect={openResult} stale />
                  ))}
                </CommandPrimitive.Group>
              ))}

              {matchingActions.length ? (
                <CommandPrimitive.Group heading="Quick actions" className={groupClass}>
                  {matchingActions.map((entry) => (
                    <EntryRow key={entry.id || entry.label} entry={entry} query={trimmed} onSelect={runEntry} testPrefix="action" />
                  ))}
                </CommandPrimitive.Group>
              ) : null}
              {matchingPages.length ? (
                <CommandPrimitive.Group heading="Go to" className={groupClass}>
                  {matchingPages.map((entry) => (
                    <EntryRow key={entry.path} entry={{ ...entry, id: entry.path }} query={trimmed} onSelect={runEntry} testPrefix="page" />
                  ))}
                </CommandPrimitive.Group>
              ) : null}
            </CommandPrimitive.List>
            <div className="hidden items-center gap-4 border-t border-line bg-surface-2 px-4 py-2 text-[12px] text-ink-3 sm:flex">
              <span className="inline-flex items-center gap-1.5"><kbd className="ui-kbd">↑</kbd><kbd className="ui-kbd">↓</kbd> move</span>
              <span className="inline-flex items-center gap-1.5"><kbd className="ui-kbd"><CornerDownLeft className="h-3 w-3" aria-hidden="true" /></kbd> open</span>
              <span className="inline-flex items-center gap-1.5"><kbd className="ui-kbd">esc</kbd> close</span>
              {!searching && recent.length ? (
                <span className="ml-auto inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" aria-hidden="true" />Recent items stay on this device</span>
              ) : null}
            </div>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export { CommandPalette, CommandPaletteTrigger, PALETTE_NAV_STATE, usePaletteNavKey }
